/**
 * article-featured-products.js
 * Swiper.js carousel init + Add-to-Cart handler
 * for the Article Featured Products section.
 *
 * Assumes Swiper CSS + JS are loaded before this file.
 * Uses the Cart API so the mini-cart / cart drawer updates
 * automatically in themes like Dawn.
 */

(function () {
  'use strict';

  /* ─── Constants ───────────────────────────────────────────── */
  const SELECTORS = {
    section:    '[data-section="article-featured-products"]',
    swiper:     '.afp-swiper',
    btnPrev:    '.afp-btn-prev',
    btnNext:    '.afp-btn-next',
    pagination: '.afp-pagination',
    atcBtn:     '[data-add-to-cart]',
    atcForm:    '.afp-card__form',
  };

  const BREAKPOINTS = {
    mobile:  480,
    tablet:  750,
    desktop: 990,
  };

  /* ─── Swiper config factory ────────────────────────────────── */
  function buildSwiperConfig(section) {
    const prev       = section.querySelector(SELECTORS.btnPrev);
    const next       = section.querySelector(SELECTORS.btnNext);
    const pagination = section.querySelector(SELECTORS.pagination);

    return {
      // Slide layout
      slidesPerView:  1.1,
      spaceBetween:   16,
      centeredSlides: false,
      grabCursor:     true,

      // Responsive breakpoints
      breakpoints: {
        [BREAKPOINTS.mobile]: {
          slidesPerView: 1.5,
          spaceBetween:  20,
        },
        [BREAKPOINTS.tablet]: {
          slidesPerView: 2.2,
          spaceBetween:  24,
          centeredSlides: false,
        },
        [BREAKPOINTS.desktop]: {
          slidesPerView: 3,
          spaceBetween:  28,
          centeredSlides: false,
        },
        1280: {
          slidesPerView: 4,
          spaceBetween:  28,
        },
      },

      // Autoplay
      autoplay: {
        delay:                3800,
        disableOnInteraction: false,
        pauseOnMouseEnter:    true,
      },

      // Loop — only if enough slides
      loop: section.querySelectorAll('.swiper-slide').length > 3,

      // Pagination
      pagination: {
        el:        pagination,
        clickable: true,
        dynamicBullets: true,
      },

      // Navigation
      navigation: {
        prevEl: prev,
        nextEl: next,
      },

      // Accessibility
      a11y: {
        enabled:           true,
        prevSlideMessage:  'Previous slide',
        nextSlideMessage:  'Next slide',
        paginationBulletMessage: 'Go to slide {{index}}',
      },

      // Keyboard control
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
    };
  }

  /* ─── Init all carousel instances ─────────────────────────── */
  function initCarousels() {
    const sections = document.querySelectorAll(SELECTORS.section);

    sections.forEach((section) => {
      const swiperEl = section.querySelector(SELECTORS.swiper);
      if (!swiperEl) return;

      // Bail if Swiper not loaded
      if (typeof Swiper === 'undefined') {
        console.warn('[AFP] Swiper.js not found. Include it in theme assets.');
        return;
      }

      const config = buildSwiperConfig(section);
      const swiper = new Swiper(swiperEl, config);

      // Pause autoplay on focus (keyboard users)
      swiperEl.addEventListener('focusin', () => swiper.autoplay.stop());
      swiperEl.addEventListener('focusout', () => swiper.autoplay.start());
    });
  }

  /* ─── Add-to-Cart handler ──────────────────────────────────── */
  function initAddToCart() {
    document.querySelectorAll(SELECTORS.atcForm).forEach((form) => {
      form.addEventListener('submit', async function (e) {
        e.preventDefault();

        const btn      = form.querySelector(SELECTORS.atcBtn);
        const variantId = form.querySelector('input[name="id"]')?.value;
        const quantity  = form.querySelector('input[name="quantity"]')?.value || 1;

        if (!variantId || !btn) return;

        // Set loading state
        setButtonState(btn, 'loading');

        try {
          const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ id: variantId, quantity: Number(quantity) }),
          });

          if (!response.ok) throw new Error('Cart add failed');

          setButtonState(btn, 'added');

          // Notify theme cart drawer / bubble — Dawn uses this event
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

          // Also dispatch the standard Shopify theme event used by Dawn
          const cartData = await fetch(window.Shopify.routes.root + 'cart.js')
            .then(r => r.json());

          document.dispatchEvent(new CustomEvent('cart:updated', {
            bubbles: true,
            detail: { cart: cartData },
          }));

          // Open cart drawer if the theme exposes one
          if (typeof window.theme !== 'undefined' && typeof window.theme.openCartDrawer === 'function') {
            window.theme.openCartDrawer();
          }

          // Reset button after delay
          setTimeout(() => setButtonState(btn, 'default'), 2400);

        } catch (err) {
          console.error('[AFP] Add to cart error:', err);
          setButtonState(btn, 'default');
        }
      });
    });
  }

  /* ─── Button state helper ──────────────────────────────────── */
  function setButtonState(btn, state) {
    const textEl    = btn.querySelector('.afp-card__atc-btn-text');
    const spinnerEl = btn.querySelector('.afp-card__atc-btn-spinner');

    btn.classList.remove('is-loading', 'is-added');
    btn.disabled = false;

    switch (state) {
      case 'loading':
        btn.classList.add('is-loading');
        btn.disabled = true;
        break;

      case 'added':
        btn.classList.add('is-added');
        if (textEl) textEl.textContent = window.AFP_i18n?.added || 'Added!';
        break;

      default:
        if (textEl) {
          textEl.textContent = window.AFP_i18n?.addToCart || 'Add to Cart';
        }
    }
  }

  /* ─── Boot ─────────────────────────────────────────────────── */
  function boot() {
    initCarousels();
    initAddToCart();
  }

  // Wait for DOM + Swiper (which may be deferred)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init if Shopify Section Events fire (theme editor support)
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector(SELECTORS.section)) boot();
  });

})();
